import type React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Center,
  Container,
  Group,
  Loader,
  SimpleGrid,
  Text,
} from "@mantine/core";
import { Alert } from "@xplatform/design-system";
import { IconAlertCircle, IconInbox } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import MappingServiceCard from "@components/MappingServiceCard/MappingServiceCard";
import { useMappingServices } from "@hooks/useMappingServices";
import "./DashboardPage.scss";

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { services, loading, error } = useMappingServices();

  const handleSelectService = (serviceId: string): void => {
    navigate(`../services/${encodeURIComponent(serviceId)}`);
  };

  return (
    <Container size="lg" className="dashboard-page">
      <Box
        className="dashboard-page__hero"
        role="region"
        aria-label={t("dashboardPage.hero.ariaLabel")}
      >
        <Text
          size="xl"
          fw={800}
          component="h1"
          className="dashboard-page__title"
        >
          {t("dashboardPage.hero.title")}
        </Text>
        <Text size="md" c="dimmed" className="dashboard-page__subtitle">
          {t("dashboardPage.hero.description")}
        </Text>
      </Box>

      {loading && (
        <Center
          mih={300}
          className="dashboard-page__loading"
          aria-busy="true"
          aria-live="polite"
        >
          <Group gap="sm">
            <Loader size="md" />
            <Text size="md">{t("dashboardPage.loading")}</Text>
          </Group>
        </Center>
      )}

      {!loading && error && (
        <Alert
          color="red"
          icon={<IconAlertCircle size={18} />}
          title={t("dashboardPage.errors.failedToLoad")}
          className="dashboard-page__alert"
          role="alert"
        >
          <Text size="sm">{error}</Text>
          <Text size="sm" mt="xs" c="dimmed">
            {t("dashboardPage.errors.refreshHelp")}
          </Text>
        </Alert>
      )}

      {!loading && !error && services.length === 0 && (
        <Center mih={240} className="dashboard-page__empty">
          <Group gap="sm">
            <IconInbox size={20} aria-hidden />
            <Text size="md" c="dimmed">
              {t("dashboardPage.empty.noServices")}
            </Text>
          </Group>
        </Center>
      )}

      {!loading && !error && services.length > 0 && (
        <SimpleGrid
          cols={{ base: 1, sm: 2, lg: 3 }}
          spacing="lg"
          className="dashboard-page__grid"
        >
          {services.map((service) => (
            <MappingServiceCard
              key={service.id}
              service={service}
              onSelect={handleSelectService}
            />
          ))}
        </SimpleGrid>
      )}
    </Container>
  );
};

export default DashboardPage;
